import { formatInTimeZone } from 'date-fns-tz';
import { parseISO, compareAsc } from 'date-fns';
import type { ScheduleEvent, Resource, DateRange } from '../types';

// ─── Shared helpers ────────────────────────────────────────────────────────

function formatDt(utc: string, tz: string): string {
  return formatInTimeZone(parseISO(utc), tz, 'yyyy-MM-dd HH:mm');
}

function resourceName(resourceId: string | undefined, resources: Resource[]): string {
  if (!resourceId) return '';
  return resources.find((r) => r.id === resourceId)?.name ?? resourceId;
}

/** Events visible in the current view range, sorted by start. */
function visibleEvents(events: ScheduleEvent[], range: DateRange): ScheduleEvent[] {
  const rangeStart = parseISO(range.startUtc).getTime();
  const rangeEnd = parseISO(range.endUtc).getTime();
  return events
    .filter((ev) => {
      const s = parseISO(ev.startUtc).getTime();
      const e = parseISO(ev.endUtc).getTime();
      return s < rangeEnd && e > rangeStart;
    })
    .sort((a, b) => compareAsc(parseISO(a.startUtc), parseISO(b.startUtc)));
}

function rows(
  events: ScheduleEvent[],
  range: DateRange,
  timezone: string,
  resources: Resource[],
): string[][] {
  return visibleEvents(events, range).map((ev) => [
    ev.title,
    formatDt(ev.startUtc, timezone),
    formatDt(ev.endUtc, timezone),
    resourceName(ev.resourceId, resources),
    ev.isAllDay ? 'Yes' : 'No',
    ev.description ?? '',
  ]);
}

const HEADERS = ['Title', 'Start', 'End', 'Resource', 'All Day', 'Description'];

function triggerDownload(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function safeFilename(prefix: string, ext: string): string {
  return `${prefix}-${new Date().toISOString().slice(0, 10)}.${ext}`;
}

// ─── CSV ───────────────────────────────────────────────────────────────────

function csvEscape(val: string): string {
  const s = val.replace(/"/g, '""');
  return /[",\n\r]/.test(s) ? `"${s}"` : s;
}

export function exportCSV(
  events: ScheduleEvent[],
  range: DateRange,
  timezone: string,
  resources: Resource[] = [],
): void {
  const data = [HEADERS, ...rows(events, range, timezone, resources)];
  const csv = data.map((r) => r.map(csvEscape).join(',')).join('\r\n');
  triggerDownload(new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' }), safeFilename('schedule', 'csv'));
}

// ─── Excel (XLSX) ──────────────────────────────────────────────────────────

export async function exportXLSX(
  events: ScheduleEvent[],
  range: DateRange,
  timezone: string,
  resources: Resource[] = [],
): Promise<void> {
  const ExcelJS = (await import('exceljs')).default;
  const workbook = new ExcelJS.Workbook();
  const ws = workbook.addWorksheet('Schedule');

  ws.columns = [
    { header: HEADERS[0], key: 'title',       width: 30 },
    { header: HEADERS[1], key: 'start',        width: 18 },
    { header: HEADERS[2], key: 'end',          width: 18 },
    { header: HEADERS[3], key: 'resource',     width: 20 },
    { header: HEADERS[4], key: 'allday',       width: 8  },
    { header: HEADERS[5], key: 'description',  width: 40 },
  ];

  ws.getRow(1).font = { bold: true };

  for (const row of rows(events, range, timezone, resources)) {
    ws.addRow(row);
  }

  const buf = await workbook.xlsx.writeBuffer();
  triggerDownload(
    new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }),
    safeFilename('schedule', 'xlsx'),
  );
}

// ─── PDF ───────────────────────────────────────────────────────────────────

export async function exportPDF(
  events: ScheduleEvent[],
  range: DateRange,
  timezone: string,
  resources: Resource[] = [],
  title = 'Schedule',
): Promise<void> {
  const { default: jsPDF } = await import('jspdf');
  const { default: autoTable } = await import('jspdf-autotable');

  const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });

  doc.setFontSize(14);
  doc.text(title, 40, 40);
  doc.setFontSize(9);
  doc.setTextColor(120);
  doc.text(
    `Exported ${new Date().toLocaleString()} · Timezone: ${timezone}`,
    40,
    56,
  );

  autoTable(doc, {
    startY: 70,
    head: [HEADERS],
    body: rows(events, range, timezone, resources),
    styles: { fontSize: 8, cellPadding: 4 },
    headStyles: { fillColor: [59, 130, 246], textColor: 255, fontStyle: 'bold' },
    alternateRowStyles: { fillColor: [248, 250, 252] },
    columnStyles: {
      0: { cellWidth: 120 },
      1: { cellWidth: 90 },
      2: { cellWidth: 90 },
      3: { cellWidth: 90 },
      4: { cellWidth: 40 },
      5: { cellWidth: 'auto' },
    },
  });

  doc.save(safeFilename('schedule', 'pdf'));
}

// ─── ICS (iCalendar) ──────────────────────────────────────────────────────

function icsDate(utc: string): string {
  // Format: 20260613T090000Z
  return parseISO(utc)
    .toISOString()
    .replace(/[-:]/g, '')
    .replace(/\.\d{3}/, '');
}

function foldLine(line: string): string {
  // iCal spec: lines > 75 octets must be folded
  const chunks: string[] = [];
  let remaining = line;
  while (remaining.length > 75) {
    chunks.push(remaining.slice(0, 75));
    remaining = ' ' + remaining.slice(75);
  }
  chunks.push(remaining);
  return chunks.join('\r\n');
}

export function exportICS(events: ScheduleEvent[], range: DateRange): void {
  const visible = visibleEvents(events, range);

  const vevents = visible.map((ev) => {
    const lines: string[] = [
      'BEGIN:VEVENT',
      `UID:${ev.id}@fulcrum-schedule`,
      `DTSTAMP:${icsDate(new Date().toISOString())}`,
    ];

    if (ev.isAllDay) {
      lines.push(`DTSTART;VALUE=DATE:${icsDate(ev.startUtc).slice(0, 8)}`);
      lines.push(`DTEND;VALUE=DATE:${icsDate(ev.endUtc).slice(0, 8)}`);
    } else {
      lines.push(`DTSTART:${icsDate(ev.startUtc)}`);
      lines.push(`DTEND:${icsDate(ev.endUtc)}`);
    }

    lines.push(foldLine(`SUMMARY:${ev.title.replace(/\n/g, '\\n')}`));
    if (ev.description) {
      lines.push(foldLine(`DESCRIPTION:${ev.description.replace(/\n/g, '\\n')}`));
    }
    if (ev.recurrenceRule) {
      lines.push(`RRULE:${ev.recurrenceRule}`);
    }

    lines.push('END:VEVENT');
    return lines.join('\r\n');
  });

  const ics = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//Fulcrum Schedule//EN',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    ...vevents,
    'END:VCALENDAR',
  ].join('\r\n');

  triggerDownload(new Blob([ics], { type: 'text/calendar;charset=utf-8;' }), safeFilename('schedule', 'ics'));
}
